// MUI components
import { useState, useContext } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import HelpIcon from '@mui/icons-material/Help'
import { IconButton, Tooltip, Fade } from '@mui/material'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftButton from 'components/SoftButton'

const Onboarding401kDetails = ({ helpText }) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)

  const [roth, setRoth] = useState(intakeState.roth)
  const [traditional, setTraditional] = useState(intakeState.traditional)
  const [rothContribution, setRothContribution] = useState(
    intakeState.rothContribution
  )
  const [traditionalContribution, setTraditionalContribution] = useState(
    intakeState.traditionalContribution
  )
  const [rothBalance, setRothBalance] = useState(intakeState.rothBalance)
  const [traditionalBalance, setTraditionalBalance] = useState(intakeState.traditionalBalance)
  const [rothContributionAmount, setRothContributionAmount] = useState(intakeState.rothContributionAmount)
  const [traditionalContributionAmount, setTraditionalContributionAmount] = useState(intakeState.traditionalContributionAmount)
  const [traditionalPercentDollarsQuestion, setTraditionalPercentDollarsQuestion] = useState(true)
  const [rothPercentDollarsQuestion, setRothPercentDollarsQuestion] = useState(true)

  const dollarsToPercent = (contributionAmount) => {
    return intakeState.baseSalary
      ? ((contributionAmount * 26) / intakeState.baseSalary) * 100
      : 0
  }
  const percentToDollars = (contributionPercent) => {
    return intakeState.baseSalary
      ? (intakeState.baseSalary / 26) * (contributionPercent / 100)
      : 0
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box mb={1}>
        <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
          Let's talk about your 401k:
        </Typography>
      </Box>
      <SoftBox component="form" py={3}>
        <Grid container columnSpacing={1}>
          {traditional === true ? (
            <>
              <Grid item xs={12} mt={2} mb={2}>
                <Typography variant="body1Medium">
                  Traditional 401k Contribution Amount {traditionalPercentDollarsQuestion ? '%' : ''}
                </Typography>
              </Grid>

              {traditionalPercentDollarsQuestion ? (
                <>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'traditionalContribution',
                      label: 'What percentage do you contribute?',
                      placeholder: 'Ex. 5.0%',
                      inputType: 'percentage',
                      value: traditionalContribution,
                      onChange: (traditionalContribution) => {
                        updateIntakeStateAtKey({
                          traditionalContribution,
                          traditionalContributionAmount:
                            percentToDollars(traditionalContribution),
                        })
                      },
                    })}
                  </Grid>
                  <Grid item xs={12} mb={2}>
                    <SoftBox display="flex" justifyContent="flex-start">
                      <SoftButton
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setTraditionalPercentDollarsQuestion(false)}
                      >
                        Use amount contributed per pay period instead
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'traditionalContributionAmount',
                      label: 'What amount do you contribute per pay period?',
                      placeholder: '$135.00',
                      inputType: 'currencyFloat',
                      value: traditionalContributionAmount,
                      onChange: (traditionalContributionAmount) => {
                        updateIntakeStateAtKey({
                          traditionalContributionAmount,
                          traditionalContribution: dollarsToPercent(
                            traditionalContributionAmount
                          ),
                        })
                      },
                    })}
                  </Grid>
                  <Grid item xs={12} mb={2}>
                    <SoftBox display="flex" justifyContent="flex-start">
                      <SoftButton
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setTraditionalPercentDollarsQuestion(true)}
                      >
                        Use percentage contributed per pay period instead
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </>
              )}

            <Grid item xs={12}>
            <Grid item xs={12} mt={2} mb={2}>
              <Typography variant="body1Medium">
                Traditional 401k Balance
              </Typography>
            </Grid>
              {renderMuiFormField({
                autoFocus: false,
                id: 'traditionalBalance',
                label: (
                  <>
                    <span>
                      What is your current Traditional 401k balance?
                      {helpText && (
                        <Tooltip
                          TransitionComponent={Fade}
                          placement="top"
                          color="info"
                          enterTouchDelay={0}
                          leaveTouchDelay={3000}
                          title={helpText}
                        >
                          <IconButton size="small" color="dark">
                            <HelpIcon sx={{ marginTop: '-10px' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </span>
                  </>
                ),
                placeholder: '$250,000.00',
                inputType: 'currencyFloat',
                value: traditionalBalance,
                onChange: (traditionalBalance) => updateIntakeStateAtKey({ traditionalBalance }),
              })}
            </Grid>
            </>
          ) : (
            <></>
          )}
          
          {roth === true ? (
            <>
              <Grid item xs={12} mt={2} mb={2}>
                <Typography variant="body1Medium">
                  Roth 401k Contribution Amount {rothPercentDollarsQuestion ? '%' : ''}
                </Typography>
              </Grid>

              {rothPercentDollarsQuestion ? (
                <>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'rothContribution',
                      label: 'What percentage do you contribute?',
                      placeholder: 'Ex. 5.0%',
                      inputType: 'percentage',
                      value: rothContribution,
                      onChange: (rothContribution) => {
                        updateIntakeStateAtKey({
                          rothContribution,
                          rothContributionAmount:
                            percentToDollars(rothContribution),
                        })
                      },
                    })}
                  </Grid>
                  <Grid item xs={12} mb={2}>
                    <SoftBox display="flex" justifyContent="flex-start">
                      <SoftButton
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setRothPercentDollarsQuestion(false)}
                      >
                        Use amount contributed per pay period instead
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'rothContributionAmount',
                      label: 'What amount do you contribute per pay period?',
                      placeholder: '$135.00',
                      inputType: 'currencyFloat',
                      value: rothContributionAmount,
                      onChange: (rothContributionAmount) => {
                        updateIntakeStateAtKey({
                          rothContributionAmount,
                          rothContribution: dollarsToPercent(
                            rothContributionAmount
                          ),
                        })
                      },
                    })}
                  </Grid>
                  <Grid item xs={12} mb={2}>
                    <SoftBox display="flex" justifyContent="flex-start">
                      <SoftButton
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setRothPercentDollarsQuestion(true)}
                      >
                        Use percentage contributed per pay period instead
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
              <Grid item xs={12} mt={2} mb={2}>
                <Typography variant="body1Medium">
                  Roth 401k Balance
                </Typography>
                </Grid>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'rothBalance',
                  label: (
                    <>
                      <span>
                        What is your current Roth 401k balance?
                        {helpText && (
                          <Tooltip
                            TransitionComponent={Fade}
                            placement="top"
                            color="info"
                            enterTouchDelay={0}
                            leaveTouchDelay={3000}
                            title={helpText}
                          >
                            <IconButton size="small" color="dark">
                              <HelpIcon sx={{ marginTop: '-10px' }} />
                            </IconButton>
                          </Tooltip>
                          )}
                        </span>
                    </>
                  ),
                  placeholder: '$250,000.00',
                  inputType: 'currencyFloat',
                  value: rothBalance,
                  onChange: (rothBalance) => updateIntakeStateAtKey({ rothBalance }),
                })}
              </Grid>
            </>
          ) : (
            <></>
          )}
            
        </Grid>
      </SoftBox>
    </Box>
  )
}

export default Onboarding401kDetails
